import { useQuery } from '@tanstack/react-query';
import { CLOUD_PROVIDERS, GCP_GPU_PRICES_URL, gcpQuotasUrl, type CloudPod, type GcpZonesResponse } from '@couch/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError, errorMessage } from '../../api/client';
import { cloudApi } from '../../api/cloud';
import { FormError, useSubmit } from '../../components/Field';
import { CloudCostWarning, ConsoleLink } from './CloudBadge';

const free = (q: { limit: number; usage: number }) => q.limit - q.usage;

/** What the project's quota allows (doc 68): Couch reads it before it asks Google for a VM. */
function QuotaLine({ zones, spot }: { zones: GcpZonesResponse; spot: boolean }) {
  const regions = [...new Set(zones.zones.filter((z) => free(spot ? z.spotQuota : z.quota) >= 1).map((z) => z.region))];
  const url = gcpQuotasUrl(zones.projectId);
  if (free(zones.gpusAllRegions) < 1) {
    return <p role="alert" className="form-error">Your project has no GPU quota yet (GPUs (all regions) = {zones.gpusAllRegions.limit}). <a href={url} target="_blank" rel="noopener noreferrer">Ask Google for 1 ↗</a></p>;
  }
  if (regions.length === 0) {
    return <p role="alert" className="form-error">No European region has quota for {spot ? 'a Spot' : 'a regular'} L4 ({spot ? 'Preemptible NVIDIA L4 GPUs' : 'NVIDIA L4 GPUs'}). <a href={url} target="_blank" rel="noopener noreferrer">Ask Google for 1, e.g. in europe-west4 ↗</a></p>;
  }
  return <p className="muted">Project {zones.projectId ?? '—'} · L4 quota in {regions.join(', ')} · Couch tries the zones there until one has an L4 free.</p>;
}

function GcpChoices({ zones, onCreated }: { zones: GcpZonesResponse; onCreated: (pod: CloudPod) => void }) {
  const [spot, setSpot] = useState(true);
  const [name, setName] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const form = useSubmit(async () => {
    const trimmed = name.trim();
    return cloudApi.createGcpMachine({ spot, acknowledged: true, ...(trimmed ? { name: trimmed } : {}) });
  }, (result) => { onCreated(result.pod); });
  return (
    <form onSubmit={form.submit} className="pod-form">
      <p>NVIDIA L4 (24 GB) · g2-standard-4 · a European zone · 100 GB disk · Google&apos;s image with the NVIDIA driver.</p>
      <label className="checkbox">
        <input type="checkbox" checked={spot} onChange={(e) => { setSpot(e.target.checked); }} />
        Spot — much cheaper; Google may stop it at any time (Start brings it back when Google has an L4 again)
      </label>
      <QuotaLine zones={zones} spot={spot} />
      <div className="field">
        <label htmlFor="vm-name">Name (optional)</label>
        <input id="vm-name" value={name} maxLength={60} placeholder="Couch L4" onChange={(e) => { setName(e.target.value); }} />
        {form.fieldErrors.name && <p role="alert" className="form-error">{form.fieldErrors.name}</p>}
      </div>
      <p className="muted">
        Google bills the VM by the second while it runs, and the 100 GB disk (roughly $10–12 a month) also while it is stopped.{' '}
        <a href={GCP_GPU_PRICES_URL} target="_blank" rel="noopener noreferrer">Google&apos;s GPU prices ↗</a>
      </p>
      <CloudCostWarning />
      <p className="muted">Couch creates the VM with your Google Cloud key (via {zones.via === 'couch' ? 'the key kept on Couch' : zones.via}). There is no spending limit for Google — only the idle stop.</p>
      <label className="checkbox">
        <input type="checkbox" checked={acknowledged} onChange={(e) => { setAcknowledged(e.target.checked); }} />
        I understand: Google charges my account. I check Google&apos;s console and bill myself.
      </label>
      <FormError message={form.formError} />
      <div className="pod-actions">
        <button type="submit" disabled={!acknowledged || form.pending}>{form.pending ? 'Checking…' : 'Create VM'}</button>
        <ConsoleLink href={CLOUD_PROVIDERS.gcp.console} provider="gcp" />
      </div>
    </form>
  );
}

/** "New cloud machine" → Google Cloud (doc 70): the project's quota first, then Spot or not, the warning, Create. */
export function GcpMachineForm({ onCreated }: { onCreated: (pod: CloudPod) => void }) {
  const zones = useQuery({ queryKey: ['gcp-zones'], queryFn: cloudApi.gcpZones, retry: false, staleTime: 60_000 });
  if (zones.isPending) return <p role="status">Reading your Google Cloud project…</p>;
  if (zones.isError) {
    const noKey = zones.error instanceof ApiError && zones.error.status === 409;
    return (
      <div role="alert" className="form-error">
        <p>{errorMessage(zones.error)}</p>
        {noKey && <p><Link to="/connections">Connections → Google Cloud</Link></p>}
      </div>
    );
  }
  return <GcpChoices zones={zones.data} onCreated={onCreated} />;
}
